import React, { useState } from "react";
import { useFormik } from "formik";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CustomModal from "../../../base-components/CustomModal";
import { adminResetMemberPasswordSchema } from "../../../schemas/memberPortalSchemas";
import { adminResetMemberPassword } from "../../../api/memberPortalClient";

function ResetMemberPasswordModal({
  isOpen,
  onClose,
  member,
  actingUserId,
  onSuccess,
  onError,
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const formik = useFormik({
    initialValues: { newPassword: "", confirmPassword: "" },
    validationSchema: adminResetMemberPasswordSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      if (!member?._id || !actingUserId) {
        onError?.("Missing member or acting user.");
        return;
      }
      setSubmitting(true);
      try {
        const { ok, body } = await adminResetMemberPassword(
          member._id,
          actingUserId,
          values.newPassword
        );
        if (!ok) {
          onError?.(body?.error?.message || "Failed to reset password.");
          return;
        }
        resetForm();
        onSuccess?.(body?.data?.message || "Password updated.");
        onClose?.();
      } catch (err) {
        onError?.(err.message || "Failed to reset password.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { values, errors, touched, handleBlur, handleChange, handleSubmit, resetForm } =
    formik;

  const handleModalClose = () => {
    resetForm();
    onClose?.();
  };

  return (
    <CustomModal isModal={isOpen} handleModal={handleModalClose} size="md">
      <div className="px-6 pb-6">
        <h2 className="text-xl font-semibold text-gx-ink mb-1">
          Reset Member Password
        </h2>
        <p className="text-sm text-gx-ink-soft mb-4">
          {member?.name
            ? `Set a new portal password for ${member.name}`
            : "Set a new portal password"}
          {member?.email ? ` (${member.email})` : ""}.
        </p>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4" autoComplete="off">
          <div>
            <label className="text-sm font-medium text-gx-ink">
              New Password <span className="text-red-600">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="newPassword"
                className={`w-full border rounded-md p-2 mt-1 outline-none ${
                  touched.newPassword && errors.newPassword ? "border-red-500" : "border-gx-border"
                }`}
                value={values.newPassword}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <button
                type="button"
                className="absolute right-3 top-3 text-gx-ink-soft"
                onClick={() => setShowPassword(!showPassword)}
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </button>
            </div>
            {touched.newPassword && errors.newPassword && (
              <p className="text-red-500 text-xs mt-1">{errors.newPassword}</p>
            )}
          </div>
          <div>
            <label className="text-sm font-medium text-gx-ink">
              Confirm Password <span className="text-red-600">*</span>
            </label>
            <input
              type={showPassword ? "text" : "password"}
              name="confirmPassword"
              className={`w-full border rounded-md p-2 mt-1 outline-none ${
                touched.confirmPassword && errors.confirmPassword
                  ? "border-red-500"
                  : "border-gx-border"
              }`}
              value={values.confirmPassword}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            {touched.confirmPassword && errors.confirmPassword && (
              <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
            )}
          </div>
          <div className="flex justify-end gap-3 mt-2">
            <button
              type="button"
              onClick={handleModalClose}
              className="px-4 py-2 rounded-md border border-gx-border text-gx-ink hover:bg-gx-canvas"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded-md bg-gx-red text-white font-medium hover:bg-[#b5141a] disabled:opacity-60"
            >
              {submitting ? "Saving…" : "Set Password"}
            </button>
          </div>
        </form>
      </div>
    </CustomModal>
  );
}

export default ResetMemberPasswordModal;
